import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BlockIcon from "@mui/icons-material/Block";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { getChemistName } from "@/utils/textUtils";
import { getAppBrand } from "@/config/appBrands";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayYmd = () => ymd(new Date());

/** Adds `n` days to today and returns YYYY-MM-DD. */
const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return ymd(d);
};

/** Adds `n` months to today and returns YYYY-MM-DD. */
const addMonths = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() + n);
  return ymd(d);
};

// Re-check presets — "when do we want to talk to this pharmacy again?".
const PRESETS = [
  { key: "1w", label: "1 week", resolve: () => addDays(7) },
  { key: "2w", label: "2 weeks", resolve: () => addDays(14) },
  { key: "1m", label: "1 month", resolve: () => addMonths(1) },
  { key: "3m", label: "3 months", resolve: () => addMonths(3) },
  { key: "6m", label: "6 months", resolve: () => addMonths(6) },
  { key: "12m", label: "12 months", resolve: () => addMonths(12) },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const p = String(iso).split("T")[0].split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0].slice(2)}` : iso;
}

/**
 * Log a check-in against a pharmacy and schedule the next one.
 *
 * A check-in records two things: the note/date of the conversation just had, and
 * when we want to come back. Until that re-check date arrives the pharmacy drops
 * off the At-Risk / Excellent worklists — the point being that those lists drain
 * to zero and only re-fill when something is actually due.
 *
 * "Stop check-ins" retires a pharmacy from the worklists entirely (sold, closed,
 * no longer using the app). It's reversible: a later check-in with a re-check
 * date puts them back in rotation.
 */
const CheckInModal = ({
  open,
  onClose,
  chemistUUID,
  chemistName,
  /** Brand key for the app this row belongs to — colours the modal. */
  app = "quda",
  /** Current schedule, so the modal can show what's already set. */
  nextCheckInDate = null,
  stopCheckIns = false,
  lastCheckInDate = null,
  /** Called after a successful save so the caller can refresh its rows. */
  onSaved,
}) => {
  const brand = getAppBrand(app);

  const [note, setNote] = useState("");
  const [checkInDate, setCheckInDate] = useState(todayYmd);
  const [preset, setPreset] = useState("1m");
  const [customDays, setCustomDays] = useState("");
  const [customDate, setCustomDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!chemistUUID) return;
    setHistoryLoading(true);
    try {
      const res = await phappsRequest(API_PATHS.CHECK_INS_CHEMIST_NOTES(chemistUUID), "GET");
      setHistory(res?.checkIns || []);
    } catch {
      setHistory([]); // history is supporting detail — never block logging a check-in
    } finally {
      setHistoryLoading(false);
    }
  }, [chemistUUID]);

  // Reset the form each time the modal opens on a (possibly different) pharmacy.
  useEffect(() => {
    if (!open) return;
    setNote("");
    setCheckInDate(todayYmd());
    setPreset(stopCheckIns ? "stop" : "1m");
    setCustomDays("");
    setCustomDate("");
    setError(null);
    loadHistory();
  }, [open, chemistUUID, stopCheckIns, loadHistory]);

  // The re-check date the current selection resolves to — null when checking in
  // without scheduling, or when the pharmacy is being retired.
  const resolvedNextDate = useMemo(() => {
    if (preset === "stop" || preset === "none") return null;
    if (preset === "days") {
      const n = parseInt(customDays, 10);
      return Number.isFinite(n) && n > 0 ? addDays(n) : null;
    }
    if (preset === "date") return customDate || null;
    return PRESETS.find((p) => p.key === preset)?.resolve() ?? null;
  }, [preset, customDays, customDate]);

  const invalidSchedule =
    (preset === "days" && !resolvedNextDate) || (preset === "date" && !customDate);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await phappsRequest(API_PATHS.CHECK_INS_CHEMIST_LOG(chemistUUID), "POST", {
        note: note.trim() || null,
        // Send the chosen date at midday to avoid timezone date-rollover; omit for
        // "today" so the server timestamps it with the exact current time.
        ...(checkInDate && checkInDate !== todayYmd()
          ? { checkInAt: new Date(`${checkInDate}T12:00:00`).toISOString() }
          : {}),
        nextCheckInDate: resolvedNextDate,
        stopCheckIns: preset === "stop",
      });
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to log check-in"));
    } finally {
      setSaving(false);
    }
  };

  const presetBtnSx = (selected, danger = false) => {
    const accent = danger ? "#b91c1c" : brand.main;
    return {
      textTransform: "none",
      fontWeight: 700,
      fontSize: "0.8rem",
      minWidth: 0,
      px: 1.5,
      py: 0.5,
      borderRadius: "6px",
      border: `1.5px solid ${selected ? accent : "#d1d5db"}`,
      backgroundColor: selected ? accent : "#fff",
      color: selected ? "#fff" : "#374151",
      "&:hover": {
        backgroundColor: selected ? accent : danger ? "#fef2f2" : brand.tint,
        border: `1.5px solid ${accent}`,
      },
    };
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2.5,
          py: 1.75,
          backgroundColor: brand.main,
          color: "#fff",
        }}
      >
        {brand.icon && (
          <Box
            component="img"
            src={brand.icon}
            alt=""
            sx={{ width: 28, height: 28, objectFit: "contain", filter: "brightness(0) invert(1)" }}
          />
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", lineHeight: 1.25 }} noWrap>
            {getChemistName(chemistName)}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", opacity: 0.85 }}>
            Last check-in {fmtDate(lastCheckInDate)}
            {stopCheckIns
              ? " · check-ins stopped"
              : nextCheckInDate
                ? ` · next due ${fmtDate(nextCheckInDate)}`
                : " · no re-check scheduled"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} sx={{ color: "#fff" }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2.5 }}>
        <TextField
          multiline
          minRows={3}
          fullWidth
          size="small"
          autoFocus
          label="What was discussed?"
          placeholder="Notes"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        <TextField
          type="date"
          size="small"
          label="Check-in date"
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: todayYmd() }}
          value={checkInDate}
          onChange={(e) => setCheckInDate(e.target.value || todayYmd())}
          sx={{ mt: 2, width: 200 }}
        />

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
          <EventRepeatOutlinedIcon sx={{ fontSize: 18, color: brand.main }} />
          <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: brand.main }}>
            When should we check in again?
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {PRESETS.map((p) => (
            <Button key={p.key} onClick={() => setPreset(p.key)} sx={presetBtnSx(preset === p.key)}>
              {p.label}
            </Button>
          ))}
          <Button onClick={() => setPreset("days")} sx={presetBtnSx(preset === "days")}>
            In … days
          </Button>
          <Button onClick={() => setPreset("date")} sx={presetBtnSx(preset === "date")}>
            Pick a date
          </Button>
          <Tooltip arrow title="Log the check-in but leave them on the worklist — they stay due.">
            <Button onClick={() => setPreset("none")} sx={presetBtnSx(preset === "none")}>
              No re-check
            </Button>
          </Tooltip>
          <Tooltip arrow title="Stop calling this pharmacy — sold, closed, or no longer using the app. Removes them from the worklist until someone checks in again.">
            <Button
              onClick={() => setPreset("stop")}
              startIcon={<BlockIcon sx={{ fontSize: 15 }} />}
              sx={presetBtnSx(preset === "stop", true)}
            >
              Stop check-ins
            </Button>
          </Tooltip>
        </Box>

        {preset === "days" && (
          <TextField
            type="number"
            size="small"
            label="Days from today"
            inputProps={{ min: 1, max: 3650 }}
            value={customDays}
            onChange={(e) => setCustomDays(e.target.value)}
            sx={{ mt: 1.5, width: 180 }}
          />
        )}
        {preset === "date" && (
          <TextField
            type="date"
            size="small"
            label="Re-check on"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: todayYmd() }}
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
            sx={{ mt: 1.5, width: 200 }}
          />
        )}

        <Box sx={{ mt: 1.5 }}>
          {preset === "stop" ? (
            <Typography variant="caption" sx={{ color: "#b91c1c", fontWeight: 700 }}>
              This pharmacy will be removed from the check-in worklists.
            </Typography>
          ) : resolvedNextDate ? (
            <Typography variant="caption" sx={{ color: brand.main, fontWeight: 700, display: "block" }}>
              Next check-in due {fmtDate(resolvedNextDate)}
              <Typography component="span" variant="caption" sx={{ color: "#6b7280", fontWeight: 500, display: "block" }}>
                A check-in task lands on your Task Manager that day.
              </Typography>
            </Typography>
          ) : preset === "none" ? (
            <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 700 }}>
              They stay on the worklist as due.
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: "#b45309", fontWeight: 700 }}>
              Choose a {preset === "days" ? "number of days" : "date"} to schedule the re-check.
            </Typography>
          )}
        </Box>

        {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
          <HistoryOutlinedIcon sx={{ fontSize: 18, color: "#6b7280" }} />
          <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 }}>
            Previous check-ins
          </Typography>
        </Box>
        {historyLoading ? (
          <Typography variant="caption" sx={{ color: "#9ca3af" }}>Loading…</Typography>
        ) : history.length === 0 ? (
          <Typography variant="caption" sx={{ color: "#9ca3af" }}>
            No check-ins logged yet.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, maxHeight: 220, overflowY: "auto", pr: 0.5 }}>
            {history.map((h) => (
              <Box key={h.id} sx={{ borderLeft: `3px solid ${brand.light}`, pl: 1.5, py: 0.25 }}>
                <Typography variant="caption" sx={{ color: "#6b7280" }}>
                  {fmtDate(h.check_in_at)}
                  {h.manager_name ? ` · ${h.manager_name}` : ""}
                  {h.stop_check_ins
                    ? " · stopped check-ins"
                    : h.next_check_in_date
                      ? ` · re-check ${fmtDate(h.next_check_in_date)}`
                      : ""}
                </Typography>
                {h.note && (
                  <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "#374151", whiteSpace: "pre-wrap" }}>
                    {h.note}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", color: "#6b7280" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || invalidSchedule}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            backgroundColor: preset === "stop" ? "#b91c1c" : brand.main,
            "&:hover": { backgroundColor: preset === "stop" ? "#991b1b" : brand.dark },
          }}
        >
          {saving ? "Saving…" : preset === "stop" ? "Check in & stop" : "Mark as checked in"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckInModal;
